/* ============================================================
 * ROBOT ERROR SUMMARY PAGE — OWNED BY "Robot Error Summary" SESSION
 * ============================================================
 * DO NOT MODIFY THIS FILE FROM OTHER CLAUDE SESSIONS.
 *
 * This file is exclusively managed by the session that built the
 * Robot Error Summary feature (search flow, smart analysis with
 * direction-aware location clustering, technician briefings,
 * field findings form). Changes made here by other sessions have
 * repeatedly broken the UX (search, sorting, flag filtering,
 * rich briefings) and must be restored manually.
 *
 * If you need to change this page: ASK THE USER FIRST to bring
 * you back to the original Robot Error Summary session.
 *
 * Related files in the same ownership scope:
 *   - src/api/dashboard.py endpoints under /api/robot-summary/*
 *   - frontend/src/services/api.js (robot-summary helpers)
 *   - src/data/field_findings.db, project_mapping.json, field_knowledge.json
 * ============================================================ */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import RobotControls from '../components/RobotControls';  // v1.10.53 — robot control buttons
import {
  getProjectsForDropdown, getProjectRobotsSummary,
  getRobotSummary, getRobotAnalysis, getRobotRCA, getRobotBriefingDetails,
  saveFieldFinding, getRobotFindings, uploadFindingPhoto,
  getTechnicians,
} from '../services/api';

// Helper: render rows with a bold line between days. ts_iso format "YYYY-MM-DDTHH:MM:SS" or "YYYY-MM-DD HH:MM:SS".
function renderRowsWithDaySeparators(rows, renderRow) {
  const out = [];
  let prevDay = null;
  rows.forEach((row, i) => {
    const ts = row.ts_iso || '';
    const day = ts.slice(0, 10); // YYYY-MM-DD
    if (prevDay && day !== prevDay) {
      out.push(<tr key={`sep-${i}`}><td colSpan={20} style={{borderTop: '3px solid #2d3436', padding: 0, height: 0}}></td></tr>);
    }
    out.push(renderRow(row, i));
    prevDay = day;
  });
  return out;
}



const sevColor = { RED: '#d63031', YELLOW: '#e17055', GREEN: '#00b894' };
const trendColor = { improving: '#00b894', worsening: '#d63031', stable: '#636e72' };
const trendIcon = { improving: '\u2193', worsening: '\u2191', stable: '\u2194' };

// === Search Bar with Project Autocomplete ===
function SearchBar({ onSearch }) {
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [robotInput, setRobotInput] = useState('');
  const ref = useRef();
  const dropdownRef = useRef();

  useEffect(() => {
    getProjectsForDropdown().then(r => setProjects(r.projects || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.length >= 2) {
      const q = query.toLowerCase();
      const list = projects.filter(p => p.name.toLowerCase().includes(q)).slice(0, 15);
      setFiltered(list);
      setShowDropdown(true);
      setHighlightIdx(list.length > 0 ? 0 : -1);
    } else {
      setFiltered([]);
      setShowDropdown(false);
      setHighlightIdx(-1);
    }
  }, [query, projects]);

  // Keep the highlighted row scrolled into view as the user arrows through
  useEffect(() => {
    if (!showDropdown || highlightIdx < 0 || !dropdownRef.current) return;
    const node = dropdownRef.current.children[highlightIdx];
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx, showDropdown]);

  const handleInputKeyDown = (e) => {
    if (!showDropdown || filtered.length === 0) {
      if (e.key === 'Enter' && selectedProject) { handleSearch(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[highlightIdx >= 0 ? highlightIdx : 0];
      if (pick) handleSelect(pick);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (p) => {
    setSelectedProject(p);
    setQuery(p.name);
    setShowDropdown(false);
    // Auto-trigger default search: if no robot number entered, show all
    // robots with errors in the last 7 days for this project.
    if (!robotInput.trim()) {
      onSearch({ project: p, single: null, robotFrom: null, robotTo: null, showAll: true });
    }
  };

  const handleSearch = () => {
    if (!selectedProject) return;
    const input = robotInput.trim();
    let robotFrom = null, robotTo = null, single = null;
    if (input.includes('-')) {
      const parts = input.split('-');
      robotFrom = parseInt(parts[0]) || null;
      robotTo = parseInt(parts[1]) || null;
    } else if (input) {
      single = parseInt(input) || null;
    }
    onSearch({ project: selectedProject, single, robotFrom, robotTo, showAll: !input });
  };

  return (
    <div style={s.searchBar}>
      <div style={s.searchField} ref={ref}>
        <label style={s.searchLabel}>Project <span style={{fontWeight: 400, color: '#636e72', marginLeft: 6}}>(type to filter, ↑↓ to pick, Enter to open)</span></label>
        <input value={query}
          onChange={e => { setQuery(e.target.value); setSelectedProject(null); }}
          onKeyDown={handleInputKeyDown}
          onFocus={() => { if (filtered.length > 0) setShowDropdown(true); }}
          placeholder="Type project name..." style={s.searchInput}
          autoComplete="off" />
        {showDropdown && filtered.length > 0 && (
          <div ref={dropdownRef} style={s.dropdown} role="listbox">
            {filtered.map((p, i) => (
              <div key={p.id}
                   role="option"
                   aria-selected={i === highlightIdx}
                   onMouseEnter={() => setHighlightIdx(i)}
                   onClick={() => handleSelect(p)}
                   style={{
                     ...s.dropdownItem,
                     background: i === highlightIdx ? '#6c5ce7' : 'transparent',
                     color: i === highlightIdx ? '#ffffff' : '#2d3436',
                     cursor: 'pointer',
                   }}>
                <span>{p.name}</span>
                <span style={{...s.dropdownMeta, color: i === highlightIdx ? 'rgba(255,255,255,0.8)' : '#636e72'}}>
                  {p.country === 'IL' ? 'Israel' : p.country === 'IN' ? 'India' : p.country}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={s.searchField}>
        <label style={s.searchLabel}>Robot # (single, range, or empty for all)</label>
        <input value={robotInput} onChange={e => setRobotInput(e.target.value)}
          placeholder="e.g. 58 or 50-100 or empty" style={{...s.searchInput, width: 200}}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }} />
      </div>
      <button onClick={handleSearch} disabled={!selectedProject} style={s.searchBtn}>Search</button>
    </div>
  );
}

// === Category color map — distinct hues for each RCA issue category.
// Avoids red/green/yellow (reserved for severity elsewhere on the page).
const CATEGORY_STYLE = {
  weak_battery:  { bg: '#ede7ff', fg: '#6c5ce7', accent: '#6c5ce7' },  // velvet
  home_flag:     { bg: '#d9fbf5', fg: '#00a39c', accent: '#00a39c' },  // teal
  end_flag:      { bg: '#cff3f0', fg: '#008c86', accent: '#008c86' },  // deep teal
  stuck_dock:    { bg: '#ffe7c2', fg: '#8e4b10', accent: '#d35400' },  // amber/brown
  multi_spot:    { bg: '#dbeafe', fg: '#1e3a8a', accent: '#1e40af' },  // navy
  obstacle:      { bg: '#dbeafe', fg: '#1e3a8a', accent: '#1e40af' },  // navy
  clean_motor:   { bg: '#ffd8ea', fg: '#9c1458', accent: '#e84393' },  // magenta
  drive_motor:   { bg: '#dfe0ff', fg: '#3f2cad', accent: '#4834d4' },  // indigo
  drive_stalls:  { bg: '#dfe0ff', fg: '#3f2cad', accent: '#4834d4' },  // indigo
  old_pcb:       { bg: '#e0e4e8', fg: '#425161', accent: '#636e72' },  // slate
  pcb_resets:    { bg: '#cfd5dc', fg: '#2f3a45', accent: '#2d3436' },  // dark slate
  comm:          { bg: '#d4e3ee', fg: '#516d7e', accent: '#7f8c8d' },  // blue-gray
  overheat:      { bg: '#ffdec2', fg: '#a5541c', accent: '#e67e22' },  // dark orange
  mostly_ok:     { bg: '#e8eef3', fg: '#5b6774', accent: '#95a5a6' },  // cool gray
  mixed:         { bg: '#e3e6e9', fg: '#4b5258', accent: '#7f8c8d' },  // gray
};
function ModeBadge({ mode }) {
  if (!mode || (mode !== 'inactiveAirtouch' && mode !== 'inactiveClient')) return null;
  const isAirtouch = mode === 'inactiveAirtouch';
  const bg = isAirtouch ? '#d63031' : '#e67e22';
  const label = isAirtouch ? '🚨 Inactive Airtouch' : '⚠️ Inactive Client';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      background: bg, color: '#fff', padding: '4px 10px', borderRadius: 4,
      fontSize: 12, fontWeight: 700, marginRight: 6, marginBottom: 4,
    }}>{label}</div>
  );
}

function IssueChip({ issue }) {
  const style = CATEGORY_STYLE[issue.category] || CATEGORY_STYLE.mixed;
  // Split the title to bold the keyword portion
  const kw = issue.keyword || '';
  const title = issue.title || '';
  const idx = kw && title ? title.toLowerCase().indexOf(kw.toLowerCase()) : -1;
  const before = idx > 0 ? title.slice(0, idx) : '';
  const hit    = idx >= 0 ? title.slice(idx, idx + kw.length) : (kw || title);
  const after  = idx >= 0 ? title.slice(idx + kw.length) : (idx >= 0 ? '' : title);
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: style.bg, padding: '4px 10px', borderRadius: 4,
      borderLeft: `3px solid ${style.accent}`,
      fontSize: 12, color: style.fg, marginRight: 6, marginBottom: 4,
      whiteSpace: 'normal', lineHeight: 1.35, maxWidth: 500,
    }}>
      {before && <span>{before}</span>}
      <strong style={{color: style.accent, fontWeight: 700}}>{hit}</strong>
      {after && <span>{after}</span>}
    </div>
  );
}

// === Skill RCA Box — structured technician-ready summary from /robot/{n}/rca ===
// v1.10.4 fix (2026-05-06): render the v1 brief that the technician
// mobile app receives. Source: /api/robot-summary/robot/{n}/analysis →
// `technician_briefing` field, produced by _format_v1_brief() over the
// orchestrated analysis dict (which includes ALL v1.10.x rules — B / C
// / D / G / H / I / B10 / C5 / C6 / D3). Replaces the legacy /rca-based
// SkillRCABox which only saw the older `_compose_rca` cascade and was
// out of sync with the per-category boxes.
//
// Rendering: parses the brief into structured lines (numbered verdicts,
// "→ action" arrows, Bring line, freshness prefix) and styles each. The
// brief text itself is reader-clean per Rule 3 — no §-numbers, error
// codes humanized.
function BriefBox({ brief }) {
  if (!brief) {
    return (
      <div style={{background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #dfe6e9'}}>
        <div style={{fontSize: 13, color: '#636e72'}}>Smart Analysis brief loading…</div>
      </div>
    );
  }
  const lines = brief.split('\n');
  const blocks = [];
  let cur = null;
  for (const ln of lines) {
    const trimmed = ln.trim();
    if (!trimmed) { if (cur) { blocks.push(cur); cur = null; } continue; }
    // Numbered verdict — "1) Title — body."
    const numMatch = trimmed.match(/^(\d+)\)\s+(.+)$/);
    if (numMatch) {
      if (cur) blocks.push(cur);
      cur = { kind: 'verdict', num: numMatch[1], text: numMatch[2], action: null };
      continue;
    }
    // Action arrow — "→ Do this."
    if (trimmed.startsWith('→')) {
      if (cur && cur.kind === 'verdict') cur.action = trimmed.slice(1).trim();
      else blocks.push({ kind: 'note', text: trimmed });
      continue;
    }
    // Bring line — "Bring: a · b · c"
    if (/^Bring\s*:/i.test(trimmed)) {
      if (cur) { blocks.push(cur); cur = null; }
      blocks.push({ kind: 'bring', text: trimmed.replace(/^Bring\s*:\s*/i, '') });
      continue;
    }
    // All-clear / freshness / mode-flag / generic line
    if (cur && cur.kind === 'verdict') {
      cur.text += ' ' + trimmed;
    } else {
      blocks.push({ kind: 'note', text: trimmed });
    }
  }
  if (cur) blocks.push(cur);

  // Per-verdict color: try to pick from text keywords. Hard rules already
  // surface RED via Amendment B / §9.x; soft signals look yellow.
  const colorFor = (text) => {
    const t = (text || '').toLowerCase();
    if (/no active issues|behaving normally|looks healthy|ran ok/i.test(t)) return 'green';
    if (/weak battery — pack collapses|stuck at|true ldo|current sensor fail|pv-charging-path|failed repeater|communication failure/i.test(t)) return 'red';
    return 'yellow';
  };
  const fg = (c) => c === 'red' ? '#d63031' : c === 'yellow' ? '#856404' : c === 'green' ? '#0d6e3a' : '#636e72';
  const bg = (c) => c === 'red' ? '#fde2e2' : c === 'yellow' ? '#fff3cd' : c === 'green' ? '#d5f5ea' : '#f5f6fa';

  return (
    <div style={{background: '#ffffff', borderRadius: 8, padding: '14px 16px', marginBottom: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.06)', border: '1px solid #e4e8ee'}}>
      {blocks.map((b, i) => {
        if (b.kind === 'verdict') {
          const c = colorFor(b.text);
          return (
            <div key={i} style={{marginBottom: 10, padding: '10px 12px', background: bg(c), borderLeft: `4px solid ${fg(c)}`, borderRadius: 4}}>
              <div style={{fontSize: 14, fontWeight: 700, color: '#2d3436', marginBottom: 4, lineHeight: 1.45}}>
                <span style={{color: fg(c), marginRight: 4}}>{b.num})</span>
                {b.text}
              </div>
              {b.action && (
                <div style={{fontSize: 13, color: fg(c), fontWeight: 600, marginTop: 4}}>
                  → {b.action}
                </div>
              )}
            </div>
          );
        }
        if (b.kind === 'bring') {
          return (
            <div key={i} style={{marginTop: 10, padding: '8px 12px', background: '#2d3436', color: '#fff', borderRadius: 4, fontSize: 13, fontWeight: 700}}>
              Bring: {b.text}
            </div>
          );
        }
        // Note / freshness / mode-flag / all-clear sentence
        const isAllClear = /no active issues/i.test(b.text);
        return (
          <div key={i} style={{
            fontSize: 13,
            color: isAllClear ? '#0d6e3a' : '#2d3436',
            background: isAllClear ? '#d5f5ea' : 'transparent',
            padding: isAllClear ? '8px 12px' : '4px 0',
            borderRadius: isAllClear ? 4 : 0,
            marginBottom: 6,
            lineHeight: 1.5,
          }}>
            {b.text}
          </div>
        );
      })}
    </div>
  );
}


function SkillRCABox({ rca }) {
  if (!rca) {
    return (
      <div style={{background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #dfe6e9'}}>
        <div style={{fontSize: 13, color: '#636e72'}}>Robot RCA loading…</div>
      </div>
    );
  }
  if (rca.error) {
    return (
      <div style={{background: '#fde2e2', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #f5b7b1'}}>
        <div style={{fontSize: 13, color: '#c0392b'}}>RCA error: {rca.error}</div>
      </div>
    );
  }
  const color = (c) => c === 'red' ? '#d63031' : c === 'yellow' ? '#e17055' : c === 'green' ? '#00b894' : '#636e72';
  const bg    = (c) => c === 'red' ? '#fde2e2' : c === 'yellow' ? '#fff3cd' : c === 'green' ? '#d5f5ea' : '#f5f6fa';
  return (
    <div style={{background: '#ffffff', borderRadius: 8, padding: '16px 18px', marginBottom: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.06)'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
        <h4 style={{margin: 0, fontSize: 16, color: '#2d3436'}}>Robot RCA — {rca.project_name}</h4>
        {rca.short_verdict && (
          <span style={{fontSize: 12, color: '#636e72'}}>{rca.short_verdict}</span>
        )}
      </div>
      {rca.mode_line && (
        <div style={{fontSize: 13, padding: '6px 10px', background: '#fff3cd', borderRadius: 4, marginBottom: 10, fontWeight: 600}}>
          {rca.mode_line}
        </div>
      )}
      {(rca.sections || []).map((sec, i) => (
        <div key={i} style={{marginBottom: 12, padding: '10px 12px', background: bg(sec.color), borderLeft: `4px solid ${color(sec.color)}`, borderRadius: 4}}>
          <div style={{fontSize: 14, fontWeight: 700, color: '#2d3436', marginBottom: 2}}>
            {sec.n}) {sec.title}
          </div>
          <div style={{height: 1, background: '#dfe6e9', margin: '4px 0 8px'}}></div>
          {sec.body && (
            <div style={{fontSize: 13, color: '#2d3436', marginBottom: 6, lineHeight: 1.5}}>{sec.body}</div>
          )}
          {sec.action && (
            <div style={{fontSize: 13, color: color(sec.color), fontWeight: 600}}>
              → {sec.action}
            </div>
          )}
        </div>
      ))}
      {rca.bring?.length > 0 && (
        <div style={{marginTop: 12, padding: '8px 12px', background: '#2d3436', color: '#fff', borderRadius: 4, fontSize: 13, fontWeight: 700}}>
          Bring: {rca.bring.join(' · ')}
        </div>
      )}
    </div>
  );
}

// === Status Badge (current robot status from MongoDB robots collection) ===
function StatusBadge({ status, event, stuckRow, stuckVeh, charging, cleaning }) {
  // Priority: explicit stuck flags > error status > operational states
  let label, color, title;
  if (stuckRow) {
    label = 'STUCK ON ROW'; color = '#d63031';
    title = 'Robot is currently stuck on a row — needs attention';
  } else if (stuckVeh) {
    label = 'STUCK ON VEHICLE'; color = '#d63031';
    title = 'Robot is currently stuck on a vehicle — needs attention';
  } else if (status === 'error') {
    label = 'ERROR'; color = '#d63031';
    title = `Robot is currently reporting an error${event ? ` (${event})` : ''}`;
  } else if (status === 'drive-forward' || status === 'drive-backwards') {
    label = status === 'drive-forward' ? 'DRIVING →' : '← DRIVING';
    color = '#0984e3';
    title = `Robot is currently ${status}`;
  } else if (cleaning || event === 'cleaning') {
    label = 'CLEANING'; color = '#00b894';
    title = 'Robot is currently cleaning';
  } else if (charging) {
    label = 'CHARGING'; color = '#fdcb6e';
    title = 'Robot is charging at dock';
  } else if (status === 'parking' || status === 'finished-cleaning') {
    label = status === 'parking' ? 'PARKING' : 'FINISHED';
    color = '#74b9ff';
    title = status === 'parking' ? 'Robot is at dock' : 'Robot finished cleaning';
  } else if (status === 'idle' || event === 'idle') {
    label = 'IDLE'; color = '#b2bec3';
    title = 'Robot is idle';
  } else if (status === 'not-cleaning') {
    label = 'NOT CLEANING'; color = '#b2bec3';
    title = 'Robot is not cleaning';
  } else {
    label = (status || 'unknown').toUpperCase();
    color = '#636e72';
    title = `Status: ${status || 'unknown'}`;
  }
  return (
    <span title={title} style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 4,
      background: color, color: 'white', fontSize: 11, fontWeight: 700,
      letterSpacing: 0.3, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

// === Robot List View (multiple robots) ===
function RobotList({ project, robots, onSelect }) {
  const isVirtual = String(project?.id || '').startsWith('virt:');
  const [hoveredRobot, setHoveredRobot] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  const handleHover = (e, robot) => {
    setHoveredRobot(robot);
    setHoverPos({ x: e.clientX + 10, y: e.clientY - 50 });
  };

  return (
    <div>
      <h3 style={s.sectionTitle}>{project.name} — {robots.length} robots with errors</h3>
      <p style={{fontSize: 13, color: '#636e72', marginTop: -8, marginBottom: 16}}>
        Showing robots with at least one RED or YELLOW error in the last 7 days.
        Robots whose only errors are flag/no-reply messages are excluded.
      </p>
      {robots.length === 0 ? (
        <div style={s.empty}>No robots with RED or YELLOW errors.</div>
      ) : (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Robot #</th>
            {!isVirtual && <th style={s.th}>Current Status</th>}
            <th style={s.th}>Errors (7d)</th>
            <th style={s.th}>RED</th>
            <th style={s.th}>YEL</th>
            <th style={s.th}>Issue (RCA)</th>
          </tr></thead>
          <tbody>
            {robots.map((r, i) => (
              <tr key={r.robot_number} style={{...(i % 2 === 0 ? s.trEven : s.trOdd), cursor: 'pointer'}}
                  onClick={() => onSelect(r)}>
                <td style={{...s.td, fontWeight: 700}}>#{r.robot_number}</td>
                {!isVirtual && (
                  <td style={s.td}>
                    <StatusBadge status={r.current_status} event={r.event_status}
                      stuckRow={r.stuck_on_row} stuckVeh={r.stuck_on_vehicle}
                      charging={r.charging} cleaning={r.cleaning} />
                  </td>
                )}
                <td style={{...s.td, textAlign: 'right', fontWeight: 600}}>{r.total_errors}</td>
                <td style={{...s.td, textAlign: 'right', color: r.red_count > 0 ? '#d63031' : '#ccc', fontWeight: 700}}>{r.red_count || '-'}</td>
                <td style={{...s.td, textAlign: 'right', color: r.yellow_count > 0 ? '#e17055' : '#ccc'}}>{r.yellow_count || '-'}</td>
                <td style={s.td}
                    onMouseEnter={e => handleHover(e, r)}
                    onMouseLeave={() => setHoveredRobot(null)}
                    onMouseMove={e => setHoverPos({ x: e.clientX + 10, y: e.clientY - 50 })}>
                  {/* RCA issues — one chip per issue. Hover popup still shows last 10 error codes. */}
                  {(r.issues && r.issues.length > 0 || r.operation_mode === 'inactiveAirtouch' || r.operation_mode === 'inactiveClient') ? (
                    <div style={{display: 'flex', flexWrap: 'wrap', maxWidth: 520}}>
                      <ModeBadge mode={r.operation_mode} />
                      {(r.issues || []).map((iss, k) => <IssueChip key={k} issue={iss} />)}
                    </div>
                  ) : r.short_verdict ? (
                    <span style={{fontSize: 12, color: '#636e72'}}>{r.short_verdict}</span>
                  ) : (
                    <span style={{fontSize: 11, color: '#b2bec3'}}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* Hover popup */}
      {hoveredRobot && hoveredRobot.error_history?.length > 0 && (
        <div style={{...s.hoverPopup, left: hoverPos.x, top: hoverPos.y}}>
          <div style={s.hoverTitle}>Robot #{hoveredRobot.robot_number} — Last 10 errors</div>
          {hoveredRobot.error_history.map((eh, i) => {
            const bg = eh.severity === 'RED' ? '#fde2e2'
                     : eh.severity === 'YELLOW' ? '#fff3cd'
                     : '#f0f0f0';
            return (
              <div key={i} style={{...s.hoverRow, background: bg, padding: '2px 6px', borderRadius: 3, marginBottom: 2}}>
                <code style={{fontSize: 10, color: '#2d3436'}}>{eh.code}</code>
                {eh.code === 'TEMPERATURE_TOO_HIGH' && eh.temperature_c != null && (
                  <span style={{fontSize: 10, fontWeight: 600, color: '#c0392b'}}>{eh.temperature_c}°C</span>
                )}
                {eh.code === 'TEMPERATURE_TOO_HIGH' && eh.minutes_from_start != null && (
                  <span style={{fontSize: 10, color: '#636e72'}}>after {eh.minutes_from_start}m</span>
                )}
                <span style={{fontSize: 10, color: '#999'}}>{eh.days_ago < 1 ? 'today' : `${Math.round(eh.days_ago)}d ago`}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// === v1.10.4 Amendment J — 8-box per-category brief grid ===
//
// Reads `briefs` from API response (`a.category_briefs`), which is an
// 8-element array in fixed order: battery, charging, drive_motor,
// clean_motor, comm, pcb, flag, mix. Renders 2 columns × 4 rows on
// desktop, stacked on narrow viewports.
//
// Each box matches the existing Battery Health style: title bar at top,
// metric line, colored status bar with summary, optional recent-events
// table. Reader-clean: backend already humanizes error codes and removes
// §-numbers; frontend just lays it out.
function CategoryBox({ brief }) {
  const stateColor = {
    failed: { bg: '#fde2e2', fg: '#d63031', icon: '⚠' },
    fyi:    { bg: '#fff3cd', fg: '#856404', icon: '⚠' },
    ok:     { bg: '#d5f5ea', fg: '#0d6e3a', icon: '✓' },
  }[brief.state] || { bg: '#f0f0f0', fg: '#636e72', icon: '·' };
  const isOk = brief.state === 'ok';
  const recent = brief.recent_events || [];

  // Per-category column headers + cell renderers.
  const renderRecentCell = (ev, key) => {
    const v = ev[key];
    if (v === null || v === undefined || v === '') return '—';
    if (key === 'soc_pct' || key === 'pct') return `${v}%`;
    if (key === 'v') return typeof v === 'number' ? `${v.toFixed(2)} V` : v;
    if (key === 'location_m') return typeof v === 'number' ? `${v.toFixed(1)} m` : v;
    if (key === 'rotor_current_a' || key === 'charging_current_a') {
      return typeof v === 'number' ? `${v.toFixed(2)} A` : v;
    }
    if (key === 'board_temp_c') return typeof v === 'number' ? `${v.toFixed(1)}°C` : v;
    if (typeof v === 'boolean') return v ? 'Y' : 'N';
    return String(v);
  };

  // Columns per box key (matches J.3 spec).
  const COLS = {
    battery:     [['ts','Time'],['code','Event'],['v','V'],['soc_pct','SoC'],['phase','Phase']],
    charging:    [['ts','Time'],['pct','SoC'],['v','V'],['charging_current_a','I'],['charging_status','Status']],
    drive_motor: [['ts','Time'],['code','Event'],['location_m','Loc'],['direction','Dir'],['phase','Phase']],
    clean_motor: [['ts','Time'],['code','Event'],['location_m','Loc'],['rotor_current_a','Rotor I']],
    comm:        [['ts','Time'],['code','Event'],['robot_speaking_in_24h','Talking 24h']],
    pcb:         [['ts','Time'],['code','Event'],['v','V'],['board_temp_c','Temp']],
    flag:        [['ts','Time'],['code','Event'],['paired_with_stall','Paired w/ stall']],
    mix:         [['ts','Time'],['code','Event']],
  };
  const cols = COLS[brief.key] || [['ts','Time'],['code','Event']];

  // v1.10.4 Amendment J — emphasize titles on yellow/red boxes so the
  // technician's eye jumps to the problematic categories immediately.
  // Failed (red) and fyi (yellow) get colored, larger, bolder titles +
  // a state-icon prefix and a 4-px left border in the state color.
  return (
    <div style={{
      ...s.infoCard,
      padding: 0,
      overflow: 'hidden',
      border: '1px solid #e4e8ee',
      borderLeft: !isOk ? `4px solid ${stateColor.fg}` : '1px solid #e4e8ee',
      borderRadius: 6,
      boxShadow: !isOk ? `0 1px 4px ${stateColor.fg}22` : 'none',
    }}>
      <div style={{
        padding: '8px 12px',
        borderBottom: '1px solid #e4e8ee',
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        background: !isOk ? `${stateColor.fg}0d` : '#fafbfc',
      }}>
        <h4 style={{
          ...s.subTitle,
          margin: 0,
          color: !isOk ? stateColor.fg : undefined,
          fontSize: !isOk ? 17 : 14,
          fontWeight: !isOk ? 800 : 600,
          letterSpacing: !isOk ? 0.2 : 'normal',
        }}>
          {!isOk && (
            <span aria-hidden="true" style={{marginRight: 6}}>{stateColor.icon}</span>
          )}
          {brief.label}
          {brief.title_extras ? <span style={{fontWeight: 400, color: '#636e72', marginLeft: 6, fontSize: 12}}>({brief.title_extras})</span> : null}
        </h4>
        <span style={{fontSize: 11, color: '#636e72'}}>{brief.metric_line}</span>
      </div>
      <div style={{
        background: stateColor.bg,
        color: stateColor.fg,
        padding: '8px 12px',
        fontSize: 13,
        lineHeight: 1.45,
      }}>
        <span style={{fontWeight: 700, marginRight: 6}}>{stateColor.icon}</span>
        {brief.summary}
      </div>
      {!isOk && recent.length > 0 && (
        <div style={{padding: '6px 12px 10px 12px'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 11}}>
            <thead>
              <tr style={{borderBottom: '1px solid #e4e8ee'}}>
                {cols.map(([k, lbl]) => (
                  <th key={k} style={{textAlign: 'left', color: '#636e72', fontWeight: 600, padding: '4px 6px'}}>
                    {lbl}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((ev, i) => (
                <tr key={i} style={{borderBottom: '1px solid #f4f6f9'}}>
                  {cols.map(([k]) => (
                    <td key={k} style={{padding: '3px 6px', color: '#2d3436', whiteSpace: 'nowrap'}}>
                      {renderRecentCell(ev, k)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CategoryGrid({ briefs }) {
  // 2 columns × 4 rows on desktop. Spec-mandated order: battery, charging,
  // drive_motor, clean_motor, comm, pcb, flag, mix. Display column layout
  // per J.2: LEFT = drive/clean/flag/mix; RIGHT = battery/charging/comm/pcb.
  const byKey = {};
  for (const b of briefs) byKey[b.key] = b;
  const left = ['drive_motor', 'clean_motor', 'flag', 'mix']
                  .map(k => byKey[k]).filter(Boolean);
  const right = ['battery', 'charging', 'comm', 'pcb']
                   .map(k => byKey[k]).filter(Boolean);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 12,
      margin: '16px 0',
    }}>
      <div style={{display: 'grid', gap: 12}}>
        {left.map(b => <CategoryBox key={b.key} brief={b} />)}
      </div>
      <div style={{display: 'grid', gap: 12}}>
        {right.map(b => <CategoryBox key={b.key} brief={b} />)}
      </div>
    </div>
  );
}


// === Robot Analysis View (single robot) ===
function RobotAnalysisView({ project, robotNumber, onBack, urlParams }) {
  const [summary, setSummary] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  // v1.10.4 fix — `rca` state retired (was sourced from /rca endpoint
  // which lacks v1.10.5 rules). Smart Analysis section now reads the
  // mobile-app brief from `analysis.v1_brief` directly. Variable kept
  // declared as null so the legacy Battery-Health-box RCA-override
  // block (which references `rca?.sections`) safely no-ops; the new
  // 8-box CategoryGrid below covers that signal.
  const rca = null;
  const [details, setDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  // v1.10.6 polish (2026-05-06 evening): independent loading flags per
  // endpoint. Page renders the summary card + severity + fleet as soon
  // as /summary returns (~1 s), and fills in the heavy 8-box CategoryGrid
  // + Smart Analysis when /analysis arrives (~3-9 s on cold path).
  // Replaces the previous Promise.all that gated the whole page on the
  // slowest call.
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    // Reset both flags whenever inputs change.
    setLoadingSummary(true);
    setLoadingAnalysis(true);
    setSummary(null);
    setAnalysis(null);

    // v1.10.4 Amendment J — pass Assignment-page URL params through so
    // the backend can populate from_assignment + assignment_return_url.
    const analysisOpts = {
      fromAssignment: urlParams?.from === 'assignment',
      returnFilter: urlParams?.return_filter || null,
      returnAnchor: urlParams?.return_anchor || null,
    };

    let cancelled = false;
    // /summary — light, 90-day totals + trend + fleet, ~1-2 s.
    getRobotSummary(robotNumber, 90, project.id)
      .then(s => { if (!cancelled) setSummary(s); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoadingSummary(false); });
    // /analysis — heavy, 8-box brief + v1 brief + §C6 peer query, ~3-9 s.
    getRobotAnalysis(robotNumber, project.id, analysisOpts)
      .then(a => { if (!cancelled) setAnalysis(a); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoadingAnalysis(false); });
    return () => { cancelled = true; };
  }, [project.id, robotNumber, urlParams?.from, urlParams?.return_filter, urlParams?.return_anchor]);

  const handleDetails = useCallback(async () => {
    if (details) { setShowDetails(!showDetails); return; }
    setLoadingDetails(true);
    try {
      const r = await getRobotBriefingDetails(robotNumber, project.id);
      setDetails(r);
      setShowDetails(true);
    } catch (e) { console.error(e); }
    finally { setLoadingDetails(false); }
  }, [robotNumber, project.id, details, showDetails]);

  // Only block when BOTH calls are still pending (first ~1 s on cold).
  // After /summary returns, render the page with /analysis sections in
  // a "Loading…" placeholder.
  if (loadingSummary && loadingAnalysis) {
    return <div style={s.loadingMsg}>Loading analysis for Robot #{robotNumber}...</div>;
  }

  const d = summary;
  const a = analysis;

  // v1.10.4 Amendment J — Assignment-page round-trip button. Visible
  // only when API response carries from_assignment:true. Click navigates
  // to assignment_return_url which preserves filter + scroll anchor.
  const assignmentReturnUrl = a?.assignment_return_url || null;
  const fromAssignment = !!a?.from_assignment;

  return (
    <div>
      <button onClick={onBack} style={s.backBtn}>&larr; Back</button>
      {fromAssignment && assignmentReturnUrl && (
        <button
          onClick={() => { window.location.href = assignmentReturnUrl; }}
          style={{
            ...s.backBtn,
            marginLeft: 8,
            background: '#fff',
            border: '1px solid #475569',
            color: '#475569',
            fontWeight: 600,
          }}
          title="Return to the Assignment page, preserving your filter and the row you came from"
        >
          ← Return to Assignment Page
        </button>
      )}

      {d && (
        <>
          {/* v1.10.53 — Robot control buttons (top-right). Additive only. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <RobotControls robotId={a?.robot_id} projectId={project.id} robotNumber={robotNumber} />
          </div>
          {/* Summary card */}
          <div style={s.summaryCard}>
            <div style={s.summaryHeader}>
              <span style={s.robotBadge}>Robot #{d.robot_number}</span>
              {d?.current_status && (
                <span style={{
                  ...s.statusBadge,
                  background: d.current_status === 'error' ? '#ffe0e0' : d.current_status === 'parking' ? '#e0f5e0' : '#f0f0f0',
                  color: d.current_status === 'error' ? '#c0392b' : d.current_status === 'parking' ? '#27ae60' : '#636e72',
                }}>
                  {d.current_status}
                </span>
              )}
              <span style={s.projectLabel}>{d.project_name}</span>
              {d?.sw_version && (
                d.sw_outdated
                  ? <span style={{...s.versionBadge, background: '#fff3cd', color: '#856404'}}>SW: {d.sw_version}</span>
                  : <span style={{...s.versionBadge, background: '#d4edda', color: '#155724'}}>updated</span>
              )}
            </div>
            <p style={s.summaryText}>{d.summary_text}</p>
          </div>

          {/* v1.10.4 Amendment J — KPI tile band (Errors 30d/60d/90d/Trend)
              REMOVED. The 8-box per-category brief below Smart Analysis
              now carries the per-category event counts in its metric
              line, and Trend is implicit in the per-event timeline. */}

          {/* Severity + Fleet */}
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24}}>
            <div>
              <h3 style={s.sectionTitle}>Severity</h3>
              <div style={{display: 'flex', gap: 12}}>
                {['RED', 'YELLOW', 'GREEN'].map(sv => (
                  <div key={sv} style={{...s.sevCard, borderTop: `4px solid ${sevColor[sv]}`, flex: 1}}>
                    <div style={{...s.sevCount, color: sevColor[sv]}}>{(d.severity[sv]||0).toLocaleString()}</div>
                    <div style={s.sevLabel}>{sv}</div>
                  </div>
                ))}
              </div>
            </div>
            {d.fleet_comparison.vs_fleet_ratio !== null && (
              <div>
                <h3 style={s.sectionTitle}>Fleet Comparison</h3>
                <div style={s.fleetCard}>
                  <div style={s.fleetStat}><span style={s.fleetLabel}>This robot</span><span style={{...s.fleetValue, fontSize: 20}}>{d.fleet_comparison.this_robot_errors_90d}</span></div>
                  <div style={s.fleetStat}><span style={s.fleetLabel}>Fleet avg</span><span style={{...s.fleetValue, fontSize: 20}}>{d.fleet_comparison.fleet_avg_errors_90d}</span></div>
                  <div style={s.fleetStat}><span style={s.fleetLabel}>vs Fleet</span><span style={{...s.fleetValue, fontSize: 20, fontWeight: 700, color: d.fleet_comparison.vs_fleet_ratio > 2 ? '#d63031' : '#00b894'}}>{d.fleet_comparison.vs_fleet_ratio}x</span></div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* === VIRTUAL PROJECT VIEW (SQLite-only, no smart analysis) === */}
      {a && a.virtual && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Error History (SQLite-only project)</h3>
          <div style={{...s.infoCard, marginBottom: 16, background: '#fff3cd', borderLeft: '4px solid #e17055'}}>
            <strong>Note:</strong> This project ({a.project_name}) does not have a MongoDB
            project record. Smart Analysis, Technician Instructions, and live Current Status
            are not available. The error history below comes directly from uploaded log files.
          </div>
          <div style={s.infoCard}>
            <h4 style={s.subTitle}>Battery Health</h4>
            <div style={{fontSize: 13, color: '#636e72', padding: '8px 0'}}>
              Not available for this virtual project — Battery Health analysis requires
              MongoDB robotreports telemetry, which is not uploaded for SQLite-only projects.
            </div>
          </div>
          {a.error_breakdown?.length > 0 && (
            <div style={{...s.infoCard, marginTop: 12}}>
              <h4 style={s.subTitle}>Error code totals ({a.days}d)</h4>
              <table style={s.table}><thead><tr>
                <th style={s.thSm}>Code</th><th style={s.thSm}>Count</th>
              </tr></thead><tbody>
                {a.error_breakdown.map((e, i) => (
                  <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                    <td style={s.tdSm}><code style={{fontSize: 11}}>{e.code}</code></td>
                    <td style={{...s.tdSm, textAlign: 'right', fontWeight: 600}}>{e.count}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}
          {a.events_history?.length > 0 && (
            <div style={{...s.infoCard, marginTop: 12}}>
              <h4 style={s.subTitle}>Events (most recent {a.events_history.length})</h4>
              <table style={s.table}><thead><tr>
                <th style={s.thSm}>Time</th>
                <th style={s.thSm}>Code</th>
                <th style={s.thSm}>Drive state</th>
                <th style={s.thSm}>Battery</th>
                <th style={s.thSm}>Voltage</th>
                <th style={s.thSm}>Distance</th>
              </tr></thead><tbody>
                {a.events_history.map((e, i) => {
                  const bg = e.severity === 'RED' ? '#fde2e2'
                           : e.severity === 'YELLOW' ? '#fff3cd'
                           : 'inherit';
                  return (
                    <tr key={i} style={{...(i % 2 === 0 ? s.trEven : s.trOdd), background: bg}}>
                      <td style={s.tdSm}>{e.ts_iso?.replace('T', ' ') || '-'}</td>
                      <td style={s.tdSm}><code style={{fontSize: 11}}>{e.code}</code></td>
                      <td style={s.tdSm}>{e.drive_state || '-'}</td>
                      <td style={s.tdSm}>{e.battery_pct != null ? `${e.battery_pct}%` : '-'}</td>
                      <td style={s.tdSm}>{e.battery_voltage != null ? `${e.battery_voltage}V` : '-'}</td>
                      <td style={s.tdSm}>{e.distance_m != null ? `${e.distance_m.toFixed(1)}m` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody></table>
            </div>
          )}
        </div>
      )}

      {/* v1.10.6 polish — placeholder for the heavy /analysis section
          while it's still loading. Shown only when /summary has arrived
          (so user already sees the page header + severity + fleet) but
          /analysis is still in flight. Disappears as soon as /analysis
          returns and the real Smart Analysis + CategoryGrid render. */}
      {loadingAnalysis && d && !d?.virtual && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Smart Analysis</h3>
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e4e8ee',
            borderRadius: 8,
            padding: '24px 18px',
            textAlign: 'center',
            color: '#636e72',
            fontSize: 13,
          }}>
            <div style={{
              display: 'inline-block',
              width: 18, height: 18,
              border: '2px solid #dfe6e9',
              borderTopColor: '#6c5ce7',
              borderRadius: '50%',
              animation: 'rs-spin 0.9s linear infinite',
              verticalAlign: 'middle', marginRight: 10,
            }} />
            Loading detailed analysis for Robot #{robotNumber}…
            <div style={{fontSize: 11, color: '#b2bec3', marginTop: 8}}>
              Smart Analysis verdict + per-category boxes will appear here.
            </div>
          </div>
          <style>{`@keyframes rs-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* === SMART ANALYSIS === (skipped for virtual projects) */}
      {a && a.root_cause && !a.virtual && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Smart Analysis</h3>

          {/* v1.10.4 fix (2026-05-06): render the v1 brief from /analysis,
              not the legacy /rca-endpoint sections. The /analysis brief is
              produced by _format_v1_brief() over the orchestrated analysis
              dict — so it includes v1.10.5 §C5/§C6/§G/§H/§B10/§I rules.
              The legacy SkillRCABox path used /rca which doesn't have
              these rules wired in, causing this section to show GREEN
              while the category boxes below correctly showed RED.
              The brief text rendered here is byte-identical to what the
              technician mobile app receives via smart_analysis_cache. */}
          <BriefBox brief={a.v1_brief || a.technician_briefing}
                     parts={a.parts_to_bring}
                     severity={(a.root_cause || {}).primary} />
          {a.from_assignment && a.assignment_return_url && null /* round-trip already handled by top-of-page button */}


          {/* v1.10.4 Amendment J — Direction-summary tile band (Forward/
              Backward/No-location) REMOVED. Direction info is now carried
              per-event in the 8-box recent-events tables (Drive Motor +
              Battery boxes show direction tag per event). */}

          {/* v1.10.4 Amendment J — 8-box per-category brief, 2 cols × 4 rows.
              Reads `a.category_briefs` produced by _compute_category_briefs
              (see backend dashboard.py). Reader-clean text per Rule 3. */}
          {a.category_briefs && a.category_briefs.length > 0 && (
            <CategoryGrid briefs={a.category_briefs} />
          )}

          {/* Location clusters */}
          {a.location_clusters?.length > 0 && (
            <div style={{marginBottom: 16}}>
              <h4 style={s.subTitle}>Error Location Clusters</h4>
              <table style={s.table}><thead><tr>
                <th style={s.thSm}>Zone</th><th style={s.thSm}>Direction</th><th style={s.thSm}>Count</th><th style={s.thSm}>Error Codes</th>
              </tr></thead><tbody>
                {a.location_clusters.slice(0, 10).map((lc, i) => (
                  <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                    <td style={{...s.tdSm, fontWeight: 600}}>{lc.zone_m}-{lc.zone_m + 10}m</td>
                    <td style={s.tdSm}><span style={{color: lc.direction === 'FWD' ? '#0984e3' : '#6c5ce7', fontWeight: 600}}>{lc.direction}</span></td>
                    <td style={{...s.tdSm, fontWeight: 700}}>{lc.count}</td>
                    <td style={s.tdSm}>{Object.entries(lc.codes).map(([c, n]) => `${c}:${n}`).join(', ')}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}

          {/* v1.10.6 polish (2026-05-06): legacy Motor Errors + Battery Health
              (18Ah) boxes REMOVED — redundant with v1.10.4 Amendment J 8-box
              CategoryGrid above. Drive Motor + Battery boxes there carry the
              same signals with v1.10.x-aligned thresholds. */}


          {/* Detailed diagnostics toggle */}
          <button onClick={handleDetails} style={s.detailsBtn} disabled={loadingDetails}>
            {loadingDetails ? 'Loading...' : showDetails ? 'Hide Detailed Diagnostics' : 'Show Detailed Diagnostics (for technicians)'}
          </button>

          {showDetails && details && (
            <div style={s.detailsSection}>
              <h4 style={s.subTitle}>Detailed Diagnostics (from logs)</h4>
              {details.battery_history?.length > 0 && (
                <div style={{marginBottom: 16}}>
                  <h5 style={s.detailLabel}>Events History</h5>
                  <table style={s.table}><thead><tr>
                    <th style={s.thSm}>Time</th><th style={s.thSm}>Voltage</th><th style={s.thSm}>Battery %</th><th style={s.thSm}>Status</th><th style={s.thSm}>Error Code</th><th style={s.thSm}>Current</th><th style={s.thSm}>Temp</th>
                  </tr></thead><tbody>
                    {renderRowsWithDaySeparators(
                      [...details.battery_history].sort((a,b) => (b.ts_iso||'').localeCompare(a.ts_iso||'')).slice(0, 30),
                      (bh, i) => (
                      <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                        <td style={s.tdSm}>{bh.ts_iso || '-'}</td>
                        <td style={s.tdSm}>{bh.voltage_now_v || bh.reading_v ? `${bh.voltage_now_v || bh.reading_v}V` : '-'}</td>
                        <td style={s.tdSm}>{bh.battery_pct != null ? `${bh.battery_pct}%` : '-'}</td>
                        <td style={{...s.tdSm, color: bh.charging_status === 'error' ? '#d63031' : 'inherit', fontWeight: bh.charging_status === 'error' ? 600 : 400}}>{bh.charging_status || '-'}</td>
                        <td style={s.tdSm}>{bh.error_code ? <code style={{fontSize: 10, color: '#d63031'}}>{bh.error_code}</code> : '-'}</td>
                        <td style={s.tdSm}>{bh.charging_current_a ? `${bh.charging_current_a}A` : '-'}</td>
                        <td style={s.tdSm}>{bh.board_temp_c ? `${bh.board_temp_c}C` : '-'}</td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              )}
              {details.reset_history?.length > 0 && (
                <div style={{marginBottom: 16}}>
                  <h5 style={s.detailLabel}>Reset History</h5>
                  <table style={s.table}><thead><tr>
                    <th style={s.thSm}>Time</th><th style={s.thSm}>Cause</th><th style={s.thSm}>Voltage</th><th style={s.thSm}>Battery %</th><th style={s.thSm}>State Before</th><th style={s.thSm}>FW</th>
                  </tr></thead><tbody>
                    {renderRowsWithDaySeparators(
                      [...details.reset_history].sort((a,b) => (b.ts_iso||'').localeCompare(a.ts_iso||'')),
                      (rh, i) => (
                      <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                        <td style={s.tdSm}>{rh.ts_iso || '-'}</td><td style={s.tdSm}>{rh.reset_cause_name || '-'}</td>
                        <td style={s.tdSm}>{rh.battery_voltage ? `${rh.battery_voltage}V` : '-'}</td>
                        <td style={s.tdSm}>{rh.battery_pct || '-'}</td>
                        <td style={s.tdSm}><code style={{fontSize: 10}}>{rh.state_before_reset || '-'}</code></td>
                        <td style={s.tdSm}>{rh.fw_ver || '-'}</td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              )}
              {details.motor_diagnostics?.length > 0 && (
                <div style={{marginBottom: 16}}>
                  <h5 style={s.detailLabel}>Motor Diagnostics</h5>
                  <table style={s.table}><thead><tr>
                    <th style={s.thSm}>Time</th><th style={s.thSm}>Error</th><th style={s.thSm}>Location</th><th style={s.thSm}>Signals (5-sec)</th>
                  </tr></thead><tbody>
                    {renderRowsWithDaySeparators(
                      [...details.motor_diagnostics].sort((a,b) => (b.ts_iso||'').localeCompare(a.ts_iso||'')).slice(0, 20),
                      (md, i) => (
                      <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                        <td style={s.tdSm}>{md.ts_iso || '-'}</td>
                        <td style={s.tdSm}><code style={{fontSize: 10, color: '#d63031'}}>{md.overcurrent_err_name || '-'}</code></td>
                        <td style={s.tdSm}>{md.location_m != null ? (
                          <span><strong>{Math.abs(md.location_m).toFixed(1)}m</strong> <span style={{color: md.location_direction === 'FWD' ? '#0984e3' : '#6c5ce7', fontWeight: 600}}>{md.location_direction || ''}</span></span>
                        ) : <span style={{color: '#b2bec3'}}>no loc</span>}</td>
                        <td style={s.tdSm}><code style={{fontSize: 10}}>{md.signals_summary || '-'}</code></td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Field Findings */}
      <FieldFindings project={project} robotNumber={robotNumber} aiDiagnosis={a?.root_cause?.primary || ''} />

      {/* Top errors table */}
      {d?.top_errors?.length > 0 && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Top Errors (90 days)</h3>
          <table style={s.table}><thead><tr>
            <th style={s.th}>Error Code</th><th style={s.th}>Description</th><th style={s.th}>Count</th><th style={s.th}>Severity</th><th style={s.th}>vs Fleet</th>
          </tr></thead><tbody>
            {d.top_errors.map((err, i) => (
              <tr key={err.code} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                <td style={s.td}><code style={s.code}>{err.code}</code></td>
                <td style={s.td}>{err.description}</td>
                <td style={{...s.td, fontWeight: 600, textAlign: 'right'}}>{err.count.toLocaleString()}</td>
                <td style={s.td}><span style={{...s.badge, background: sevColor[err.severity?.toUpperCase()] || '#636e72'}}>{err.severity?.toUpperCase()}</span></td>
                <td style={{...s.td, textAlign: 'right'}}>
                  {err.vs_fleet != null ? <span style={{fontWeight: 700, color: err.vs_fleet > 3 ? '#d63031' : err.vs_fleet > 1.5 ? '#e17055' : '#00b894'}}>{err.vs_fleet}x</span> : '-'}
                </td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}
    </div>
  );
}


const ROOT_CAUSES = [
  "bridge", "drive_motor", "chain", "axle_pin", "actuator", "battery", "pcb",
  "rotor_motor", "magnet_sensor", "cable_connector", "microfiber", "grass_debris",
  "flag_position", "weather", "software_bug", "table_infrastructure", "solar_panel", "other"
];

function FieldFindings({ project, robotNumber, aiDiagnosis }) {
  const [open, setOpen] = useState(false);
  const [findings, setFindings] = useState([]);
  const [aiCorrect, setAiCorrect] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [parts, setParts] = useState([]);
  const [fixed, setFixed] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [assignTech, setAssignTech] = useState('');
  const [techList, setTechList] = useState([]);

  useEffect(() => {
    getRobotFindings(robotNumber, project.id).then(r => setFindings(r.findings || [])).catch(() => {});
    // Load technicians filtered by project country (e.g. Israel for Hatzerim, India for Amplus)
    const country = project?.country || '';
    getTechnicians(null, country)
      .then(d => setTechList(d.technicians || []))
      .catch(() => setTechList([]));
  }, [robotNumber, project.id, project.country]);

  const togglePart = (p) => setParts(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let photoPath = '';
      if (photo) {
        const up = await uploadFindingPhoto(photo);
        photoPath = up.photo_path;
      }
      await saveFieldFinding({
        robot_number: robotNumber,
        project_id: project.id,
        project_name: project.name,
        ai_diagnosis: aiDiagnosis || '',
        ai_was_correct: aiCorrect,
        actual_root_cause: rootCause,
        parts_replaced: parts,
        fixed: fixed,
        notes: notes,
        technician_name: assignTech || '',
      });
      setSaved(true);
      const updated = await getRobotFindings(robotNumber, project.id);
      setFindings(updated.findings || []);
      // Reset form
      setAiCorrect(''); setRootCause(''); setParts([]); setFixed(''); setNotes(''); setPhoto(null);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div style={{marginTop: 24}}>
      <button onClick={() => setOpen(!open)} style={{...s.detailsBtn, background: open ? '#6c5ce7' : '#f5f6fa', color: open ? '#fff' : '#636e72'}}>
        {open ? 'Close Field Findings' : 'Report Field Findings'}
      </button>

      {open && (
        <div style={{marginTop: 16, background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '2px solid #6c5ce7'}}>
          <h4 style={{...s.subTitle, color: '#6c5ce7', marginBottom: 16}}>Field Findings — Robot #{robotNumber}</h4>

          {/* AI Correctness */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>Was the AI diagnosis correct?</label>
            <div style={{display: 'flex', gap: 8, marginTop: 6}}>
              {[['correct', 'Correct', '#00b894'], ['partial', 'Partially', '#e17055'], ['wrong', 'Wrong', '#d63031']].map(([val, label, color]) => (
                <button key={val} onClick={() => setAiCorrect(val)}
                  style={{padding: '8px 20px', borderRadius: 8, border: aiCorrect === val ? `2px solid ${color}` : '2px solid #dfe6e9',
                    background: aiCorrect === val ? color : '#fff', color: aiCorrect === val ? '#fff' : '#2d3436',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif"}}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Assign to Technician */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>
              Assign to technician
              {project?.country && <span style={{color: '#636e72', fontWeight: 400, marginLeft: 6}}>({project.country} technicians)</span>}
            </label>
            <select value={assignTech} onChange={e => setAssignTech(e.target.value)}
              style={{...s.searchInput, width: '100%', marginTop: 6}}>
              <option value="">Select technician...</option>
              {techList.map(t => (
                <option key={t.id} value={t.fullName}>
                  {t.fullName}{t.phoneNumber ? ` (${t.phoneNumber})` : ''}
                </option>
              ))}
            </select>
            {techList.length === 0 && project?.country && (
              <div style={{fontSize: 11, color: '#d63031', marginTop: 4}}>
                No {project.country} technicians found in the directory.
              </div>
            )}
          </div>

          {/* Root Cause */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>Actual root cause</label>
            <select value={rootCause} onChange={e => setRootCause(e.target.value)}
              style={{...s.searchInput, width: '100%', marginTop: 6}}>
              <option value="">Select...</option>
              {ROOT_CAUSES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          {/* Parts Replaced */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>Parts replaced</label>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6}}>
              {ROOT_CAUSES.filter(c => !['weather','software_bug','other','flag_position','grass_debris'].includes(c)).map(p => (
                <button key={p} onClick={() => togglePart(p)}
                  style={{padding: '4px 12px', borderRadius: 16, fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif",
                    border: parts.includes(p) ? '2px solid #6c5ce7' : '1px solid #dfe6e9',
                    background: parts.includes(p) ? '#f0edff' : '#fff', color: parts.includes(p) ? '#6c5ce7' : '#636e72'}}>
                  {p.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Fixed? */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>Was the issue fixed?</label>
            <div style={{display: 'flex', gap: 8, marginTop: 6}}>
              {[['yes', 'Yes'], ['partial', 'Partially'], ['no', 'No']].map(([val, label]) => (
                <button key={val} onClick={() => setFixed(val)}
                  style={{padding: '8px 20px', borderRadius: 8, border: fixed === val ? '2px solid #6c5ce7' : '2px solid #dfe6e9',
                    background: fixed === val ? '#6c5ce7' : '#fff', color: fixed === val ? '#fff' : '#2d3436',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif"}}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Describe what you found and what you did..."
              style={{...s.searchInput, width: '100%', height: 80, resize: 'vertical', marginTop: 6}} />
          </div>

          {/* Photo */}
          <div style={{marginBottom: 16}}>
            <label style={s.searchLabel}>Photo (optional)</label>
            <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])}
              style={{marginTop: 6, fontSize: 13}} />
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={saving || !rootCause}
            style={{...s.searchBtn, opacity: (!rootCause || saving) ? 0.5 : 1}}>
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Submit Finding'}
          </button>
          {saved && <span style={{marginLeft: 12, color: '#00b894', fontWeight: 600}}>Finding saved successfully</span>}

          {/* Past findings */}
          {findings.length > 0 && (
            <div style={{marginTop: 24, borderTop: '2px solid #f1f2f6', paddingTop: 16}}>
              <h5 style={s.detailLabel}>Past Findings</h5>
              {findings.map(f => (
                <div key={f.id} style={{background: '#fafbfc', borderRadius: 8, padding: '12px 16px', marginBottom: 8, borderLeft: `4px solid ${f.ai_was_correct === 'correct' ? '#00b894' : f.ai_was_correct === 'wrong' ? '#d63031' : '#e17055'}`}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4}}>
                    <span style={{fontWeight: 700, fontSize: 13}}>{f.actual_root_cause?.replace(/_/g, ' ') || 'Unknown'}</span>
                    <span style={{fontSize: 11, color: '#636e72'}}>{f.timestamp?.split('T')[0] || ''} | {f.technician_name}</span>
                  </div>
                  <div style={{fontSize: 12, color: '#636e72'}}>
                    AI: {f.ai_was_correct} | Fixed: {f.fixed} | Parts: {f.parts_replaced?.join(', ') || 'none'}
                  </div>
                  {f.notes && <div style={{fontSize: 12, color: '#2d3436', marginTop: 4}}>{f.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// === Main Page ===
export default function RobotSummary() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState('search'); // search | list | analysis
  const [project, setProject] = useState(null);
  const [robots, setRobots] = useState([]);
  const [robotNumber, setRobotNumber] = useState(null);
  const [loading, setLoading] = useState(false);

  // When arrived from Assignment page (or any page) with ?project=<id>&robot=<n>,
  // auto-load that specific robot's analysis view. from=<page> controls the
  // "Back to …" button label + destination.
  const urlProjectId = searchParams.get('project');
  const urlRobot = searchParams.get('robot');
  const fromPage = searchParams.get('from');

  useEffect(() => {
    if (!urlProjectId || !urlRobot) return;
    let cancelled = false;
    // Pull the project list once, find the matching project, then set state.
    getProjectsForDropdown()
      .then(r => {
        if (cancelled) return;
        const proj = (r.projects || []).find(p => p.id === urlProjectId);
        if (proj) {
          setProject(proj);
          setRobotNumber(Number(urlRobot));
          setView('analysis');
        }
      })
      .catch(e => console.error('Could not resolve project from URL', e));
    return () => { cancelled = true; };
  }, [urlProjectId, urlRobot]);

  const handleBackToSource = useCallback(() => {
    if (fromPage === 'assignment') {
      navigate(`/assignments${urlProjectId ? `?project=${urlProjectId}` : ''}`);
    } else {
      navigate(-1);
    }
  }, [fromPage, urlProjectId, navigate]);

  const handleSearch = useCallback(async ({ project: proj, single, robotFrom, robotTo, showAll }) => {
    setProject(proj);
    if (single) {
      setRobotNumber(single);
      setView('analysis');
    } else {
      setLoading(true);
      try {
        const r = await getProjectRobotsSummary(proj.id, showAll ? null : robotFrom, showAll ? null : robotTo, 7);
        setRobots(r.robots || []);
        setView('list');
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
  }, []);

  const handleSelectRobot = useCallback((r) => {
    setRobotNumber(r.robot_number);
    setView('analysis');
  }, []);

  const handleBack = useCallback(() => {
    if (view === 'analysis' && robots.length > 0) setView('list');
    else { setView('search'); setRobots([]); setProject(null); }
  }, [view, robots]);

  return (
    <div style={s.page}>
      {fromPage && (
        <button
          onClick={handleBackToSource}
          style={{
            background: '#f1f2f6',
            border: '1px solid #dfe6e9',
            borderRadius: 6,
            padding: '6px 14px',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            color: '#2d3436',
            fontFamily: "'Inter', sans-serif",
            marginBottom: 12,
          }}
        >
          &larr; Back to {fromPage === 'assignment' ? 'Assignment' : 'previous page'}
        </button>
      )}
      <h1 style={s.title}>
        Robot Error Summary
        {view === 'analysis' && robotNumber && (
          <span style={{color: '#6c5ce7', marginLeft: 8}}>— Robot #{robotNumber}</span>
        )}
      </h1>

      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <button onClick={() => { setView('search'); setRobots([]); setProject(null); }} style={view !== 'search' ? s.bcLink : s.bcCurrent}>Search</button>
        {project && view !== 'search' && (<><span style={s.bcSep}> &gt; </span>
          <button onClick={() => robots.length > 0 ? setView('list') : null} style={view === 'list' ? s.bcCurrent : view === 'analysis' && robots.length > 0 ? s.bcLink : s.bcCurrent}>{project.name}</button></>)}
        {view === 'analysis' && robotNumber && (<><span style={s.bcSep}> &gt; </span><span style={s.bcCurrent}>Robot #{robotNumber}</span></>)}
      </div>

      {/* Search bar always visible */}
      <SearchBar onSearch={handleSearch} />

      {loading && <div style={s.loadingMsg}>Loading robots...</div>}

      {view === 'list' && project && !loading && (
        <RobotList project={project} robots={robots} onSelect={handleSelectRobot} />
      )}

      {view === 'analysis' && project && robotNumber && (
        <RobotAnalysisView
          project={project}
          robotNumber={robotNumber}
          onBack={handleBack}
          urlParams={{
            from: searchParams.get('from'),
            return_filter: searchParams.get('return_filter'),
            return_anchor: searchParams.get('return_anchor'),
          }}
        />
      )}

      {view === 'search' && !loading && (
        <div style={s.empty}>Search for a project above, then enter a robot number or range.</div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, fontFamily: "'Inter', -apple-system, sans-serif" },
  title: { fontSize: 26, fontWeight: 700, color: '#2d3436', margin: '0 0 8px' },
  breadcrumb: { marginBottom: 16, fontSize: 14 },
  bcSep: { color: '#b2bec3', margin: '0 4px' },
  bcLink: { background: 'none', border: 'none', color: '#6c5ce7', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: 0, fontFamily: "'Inter', sans-serif" },
  bcCurrent: { color: '#2d3436', fontWeight: 700, background: 'none', border: 'none', fontSize: 14, padding: 0, fontFamily: "'Inter', sans-serif" },
  // Search
  searchBar: { display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap' },
  searchField: { position: 'relative', display: 'flex', flexDirection: 'column', gap: 4 },
  searchLabel: { fontSize: 11, fontWeight: 600, color: '#636e72', textTransform: 'uppercase' },
  searchInput: { padding: '10px 14px', fontSize: 14, border: '2px solid #dfe6e9', borderRadius: 8, width: 300, outline: 'none', fontFamily: "'Inter', sans-serif" },
  searchBtn: { padding: '10px 24px', fontSize: 14, fontWeight: 600, background: '#6c5ce7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: "'Inter', sans-serif", height: 42 },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '2px solid #dfe6e9', borderTop: 'none', borderRadius: '0 0 8px 8px', maxHeight: 250, overflowY: 'auto', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  dropdownItem: { padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontSize: 14, borderBottom: '1px solid #f1f2f6' },
  dropdownMeta: { fontSize: 11, color: '#636e72' },
  loadingMsg: { textAlign: 'center', padding: 40, color: '#636e72' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#636e72', fontSize: 15 },
  backBtn: { background: 'none', border: 'none', color: '#6c5ce7', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '0 0 12px', fontFamily: "'Inter', sans-serif" },
  // Summary
  summaryCard: { background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: '5px solid #6c5ce7' },
  summaryHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  robotBadge: { background: '#6c5ce7', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 14, fontWeight: 700 },
  projectLabel: { fontSize: 14, color: '#636e72' },
  statusBadge: { fontSize: 11, padding: '3px 10px', borderRadius: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  versionBadge: { fontSize: 11, padding: '3px 8px', borderRadius: 12, background: '#dfe6e9', color: '#636e72', fontWeight: 600 },
  summaryText: { fontSize: 15, lineHeight: 1.6, color: '#2d3436', margin: 0 },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  kpiLabel: { fontSize: 12, color: '#636e72', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 },
  kpiValue: { fontSize: 28, fontWeight: 700, color: '#2d3436' },
  kpiSub: { fontSize: 11, color: '#636e72', marginTop: 4 },
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#2d3436', marginBottom: 12 },
  subTitle: { fontSize: 14, fontWeight: 600, color: '#2d3436', marginBottom: 8 },
  sevCard: { background: '#fff', borderRadius: 10, padding: '16px 20px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sevCount: { fontSize: 30, fontWeight: 700 },
  sevLabel: { fontSize: 12, fontWeight: 600, color: '#636e72', marginTop: 4 },
  fleetCard: { display: 'flex', gap: 20, background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  fleetStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1 },
  fleetLabel: { fontSize: 11, color: '#636e72', fontWeight: 600 },
  fleetValue: { fontSize: 22, fontWeight: 700, color: '#2d3436' },
  // Analysis
  analysisCard: { background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16 },
  rootCauseHeader: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  miniCard: { background: '#fff', borderRadius: 10, padding: '16px 20px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  infoCard: { background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  infoLine: { fontSize: 13, color: '#2d3436', padding: '4px 0' },
  alertLine: { fontSize: 13, fontWeight: 700, color: '#d63031', padding: '6px 10px', borderRadius: 6, marginBottom: 6 },
  techCard: { background: '#2d3436', borderRadius: 12, padding: '20px 24px', marginBottom: 16 },
  techText: { fontSize: 13, lineHeight: 1.7, color: '#dfe6e9', margin: 0, whiteSpace: 'pre-wrap', fontFamily: "'Inter', sans-serif" },
  partsCard: { background: '#f0f7ff', border: '1px solid #74b9ff', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#0984e3', marginBottom: 16 },
  detailsBtn: { padding: '10px 20px', fontSize: 13, fontWeight: 600, background: '#f5f6fa', color: '#636e72', border: '2px solid #dfe6e9', borderRadius: 8, cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  detailsSection: { marginTop: 16, background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #dfe6e9' },
  detailLabel: { fontSize: 13, fontWeight: 700, color: '#636e72', marginBottom: 8, textTransform: 'uppercase' },
  // Error chips in list view
  errorChip: { display: 'flex', alignItems: 'center', gap: 4, background: '#f5f6fa', padding: '3px 8px', borderRadius: 6 },
  // Hover popup
  hoverPopup: { position: 'fixed', background: '#2d3436', color: '#fff', borderRadius: 10, padding: '12px 16px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxWidth: 350, pointerEvents: 'none' },
  hoverTitle: { fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#dfe6e9' },
  hoverRow: { display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0', fontSize: 11 },
  // Tables
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#636e72', textTransform: 'uppercase', background: '#f8f9fa', borderBottom: '2px solid #dfe6e9' },
  thSm: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#636e72', textTransform: 'uppercase', background: '#f8f9fa', borderBottom: '2px solid #dfe6e9' },
  td: { padding: '12px 16px', fontSize: 14, color: '#2d3436', borderBottom: '1px solid #f1f2f6' },
  tdSm: { padding: '6px 10px', fontSize: 11, color: '#2d3436', borderBottom: '1px solid #f1f2f6', fontFamily: "'SF Mono', monospace" },
  trEven: { background: '#fff' },
  trOdd: { background: '#fafbfc' },
  code: { fontFamily: "'SF Mono', monospace", fontSize: 12, background: '#f1f2f6', padding: '2px 6px', borderRadius: 4 },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, color: '#fff', fontSize: 11, fontWeight: 700 },
};
